import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, Crown, Target } from 'lucide-react';
import type { AnalysisResult } from '@/lib/analysis-engine';

interface AnalysisLine {
  move: string;
  evaluation: number;
  pv: string[];
  depth: number;
  isMain?: boolean;
}

interface MultiLineAnalysisProps {
  analysis: AnalysisResult | null;
  isAnalyzing: boolean;
}

export function MultiLineAnalysis({ analysis, isAnalyzing }: MultiLineAnalysisProps) {
  
  const formatEvaluation = (evaluation: number): { text: string; color: string; icon: React.ReactNode } => {
    if (Math.abs(evaluation) > 900) {
      const mateIn = Math.abs(Math.round((1000 - Math.abs(evaluation))));
      return {
        text: evaluation > 0 ? `+M${mateIn}` : `-M${mateIn}`,
        color: evaluation > 0 ? 'text-green-600' : 'text-red-600',
        icon: <Crown className="h-3 w-3" />
      };
    }
    
    const evalText = evaluation > 0 ? `+${(evaluation / 100).toFixed(2)}` : `${(evaluation / 100).toFixed(2)}`;
    const absEval = Math.abs(evaluation);
    
    if (absEval > 200) {
      return {
        text: evalText,
        color: evaluation > 0 ? 'text-green-600' : 'text-red-600',
        icon: evaluation > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />
      };
    } else if (absEval > 100) {
      return {
        text: evalText,
        color: evaluation > 0 ? 'text-green-500' : 'text-red-500',
        icon: evaluation > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />
      };
    } else if (absEval > 50) {
      return {
        text: evalText,
        color: evaluation > 0 ? 'text-green-400' : 'text-orange-500',
        icon: evaluation > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />
      };
    }
    
    return {
      text: evalText,
      color: 'text-muted-foreground',
      icon: <Minus className="h-3 w-3" />
    };
  };

  // Generate multiple analysis lines (simulated for now - would come from MultiPV)
  const generateAnalysisLines = (analysis: AnalysisResult): AnalysisLine[] => {
    if (!analysis?.bestMove) return [];
    
    const lines: AnalysisLine[] = [
      {
        move: analysis.bestMove,
        evaluation: analysis.evaluation,
        pv: analysis.pv || [],
        depth: analysis.depth,
        isMain: true
      }
    ];

    // Add simulated alternative lines with slight variations
    if (analysis.evaluation !== 0) {
      lines.push({
        move: analysis.bestMove.replace(/[a-h][1-8]/, (match) => {
          const file = match[0];
          const rank = match[1];
          const nextFile = String.fromCharCode(file.charCodeAt(0) + 1);
          return nextFile <= 'h' ? nextFile + rank : match;
        }),
        evaluation: analysis.evaluation - (analysis.evaluation > 0 ? 20 : -20),
        pv: analysis.pv?.slice(1) || [],
        depth: analysis.depth - 1
      });
    }

    return lines.slice(0, 3); // Show top 3 lines
  };

  const analysisLines = analysis ? generateAnalysisLines(analysis) : [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="h-4 w-4" />
          Engine Lines
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {isAnalyzing ? (
          <div className="space-y-3">
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted/60 rounded w-1/2"></div>
            </div>
            <div className="animate-pulse delay-100">
              <div className="h-4 bg-muted rounded w-2/3 mb-2"></div>
              <div className="h-3 bg-muted/60 rounded w-1/3"></div>
            </div>
          </div>
        ) : analysisLines.length > 0 ? (
          <div className="space-y-3">
            {analysisLines.map((line, index) => {
              const evalData = formatEvaluation(line.evaluation);
              return (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border transition-all hover:bg-accent/30 ${
                    line.isMain ? 'bg-primary/5 border-primary/20' : 'bg-muted/20 border-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={line.isMain ? "default" : "outline"}
                        className="text-xs px-2 py-0"
                      >
                        {index + 1}
                      </Badge>
                      <span className="font-mono font-medium text-sm">
                        {line.move}
                      </span>
                    </div>
                    
                    <div className={`flex items-center gap-1 ${evalData.color}`}>
                      {evalData.icon}
                      <span className="font-mono text-sm font-bold">
                        {evalData.text}
                      </span>
                    </div>
                  </div>
                  
                  {line.pv.length > 0 && (
                    <div className="text-xs text-muted-foreground font-mono">
                      {line.pv.slice(0, 6).join(' ')}
                      {line.pv.length > 6 && <span className="opacity-60"> ...</span>}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>Depth {line.depth}</span>
                    {line.isMain && (
                      <Badge variant="secondary" className="text-xs">
                        Main line
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Analysis lines will appear here</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}